import React, { useState, useEffect, useCallback } from 'react';
import { Search, X, GitMerge, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { BRAND } from './ImpactDashboardTab';

const STATUS_LABEL = { not_recorded: 'Not recorded', attended: 'Attended', partially_attended: 'Partially attended', no_show: 'No-show', excused: 'Excused' };

export default function ImpactPeople({ API_URL, getAuthHeader, refreshKey }) {
  const [view, setView] = useState('people');
  const [people, setPeople] = useState({ people: [], total: 0 });
  const [orgs, setOrgs] = useState([]);
  const [dupes, setDupes] = useState({ groups: [] });
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState(null);

  const fetchPeople = useCallback(async () => {
    const qs = new URLSearchParams({ page: String(page), limit: '50' });
    if (search) qs.set('search', search);
    try {
      const r = await fetch(`${API_URL}/api/impact/people?${qs}`, { headers: getAuthHeader() });
      if (r.ok) setPeople(await r.json());
    } catch { /* noop */ }
  }, [API_URL, getAuthHeader, search, page]);

  const fetchOrgs = useCallback(async () => {
    const qs = search ? `?search=${encodeURIComponent(search)}` : '';
    try {
      const r = await fetch(`${API_URL}/api/impact/orgs${qs}`, { headers: getAuthHeader() });
      if (r.ok) setOrgs(await r.json());
    } catch { /* noop */ }
  }, [API_URL, getAuthHeader, search]);

  const fetchDupes = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/impact/duplicates`, { headers: getAuthHeader() });
      if (r.ok) setDupes(await r.json());
    } catch { /* noop */ }
  }, [API_URL, getAuthHeader]);

  useEffect(() => {
    if (view === 'people') fetchPeople();
    if (view === 'orgs') fetchOrgs();
    if (view === 'duplicates') fetchDupes();
  }, [view, fetchPeople, fetchOrgs, fetchDupes, refreshKey]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setDetail(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const openDetail = async (id) => {
    const r = await fetch(`${API_URL}/api/impact/people/${id}`, { headers: getAuthHeader() });
    if (r.ok) setDetail(await r.json());
  };

  const merge = async (primaryId, duplicateId) => {
    if (!window.confirm('Merge these records? Participation history combines under the primary record. This is reversible.')) return;
    const r = await fetch(`${API_URL}/api/impact/people/merge`, {
      method: 'POST', headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ primary_id: primaryId, duplicate_id: duplicateId })
    });
    if (r.ok) { toast.success('Records merged (reversible)'); fetchDupes(); fetchPeople(); }
    else { const d = await r.json().catch(() => ({})); toast.error(d.detail || 'Merge failed'); }
  };

  return (
    <div className="space-y-4" data-testid="impact-people">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
          {[['people', 'Participants'], ['orgs', 'Organizations'], ['duplicates', `Duplicate review${dupes.groups.length ? ` (${dupes.groups.length})` : ''}`]].map(([k, label]) => (
            <button key={k} onClick={() => { setView(k); setPage(1); }} data-testid={`impact-people-view-${k}`}
              className="px-4 py-1.5 rounded-md text-xs font-bold transition-all"
              style={view === k ? { background: '#fff', color: BRAND.navy, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' } : { color: '#64748b' }}>
              {label}
            </button>
          ))}
        </div>
        {view !== 'duplicates' && (
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder={view === 'people' ? 'Search name or email…' : 'Search organizations…'}
              data-testid="impact-people-search" className="pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm w-64" />
          </div>
        )}
      </div>

      {view === 'people' && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm" data-testid="impact-people-table">
            <thead><tr className="text-left text-xs uppercase text-slate-500 border-b bg-slate-50">
              <th className="py-3 px-4">Name</th><th className="px-2">Email</th><th className="px-2">Organization(s)</th><th className="px-2">Programs</th><th className="px-2">Participations</th>
            </tr></thead>
            <tbody>
              {people.people.length === 0 && <tr><td colSpan={5} className="py-10 text-center text-slate-400">No participants found.</td></tr>}
              {people.people.map(p => (
                <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => openDetail(p.id)} data-testid={`impact-person-row-${p.id}`}>
                  <td className="py-3 px-4 font-medium" style={{ color: BRAND.navy }}>{p.name}{p.no_email && <span className="ml-2 text-[10px] font-bold text-amber-600">No email</span>}</td>
                  <td className="px-2 text-xs text-slate-500">{p.email || '—'}</td>
                  <td className="px-2 text-xs">{(p.organizations || []).join('; ') || '—'}</td>
                  <td className="px-2 text-xs">{(p.programs || []).length}</td>
                  <td className="px-2">{p.participation_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center justify-between px-4 py-3 text-xs text-slate-500">
            <span>{people.total} participant(s)</span>
            <span className="flex gap-2">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 border rounded disabled:opacity-40">Prev</button>
              <button disabled={page * 50 >= people.total} onClick={() => setPage(p => p + 1)} className="px-3 py-1 border rounded disabled:opacity-40">Next</button>
            </span>
          </div>
        </div>
      )}

      {view === 'orgs' && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm" data-testid="impact-orgs-table">
            <thead><tr className="text-left text-xs uppercase text-slate-500 border-b bg-slate-50"><th className="py-3 px-4">Organization / site</th><th className="px-2">Participants</th></tr></thead>
            <tbody>
              {orgs.length === 0 && <tr><td colSpan={2} className="py-10 text-center text-slate-400">No organizations found.</td></tr>}
              {orgs.map((o, i) => (
                <tr key={i} className="border-b border-slate-100"><td className="py-3 px-4 font-medium" style={{ color: BRAND.navy }}>{o.name}</td><td className="px-2">{o.people_count}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {view === 'duplicates' && (
        <div className="space-y-4" data-testid="impact-duplicates">
          <p className="text-xs text-slate-500">People sharing the same name but different records. Review before merging — merges are reversible and never automatic.</p>
          {dupes.groups.length === 0 && <div className="bg-white rounded-xl border border-slate-200 p-10 text-center text-slate-400 text-sm">No duplicate candidates found.</div>}
          {dupes.groups.map((g, i) => (
            <div key={i} className="bg-white rounded-xl border border-amber-200 p-4" data-testid={`impact-dupe-group-${i}`}>
              <p className="text-sm font-bold mb-3 capitalize" style={{ color: BRAND.navy }}>{g.name}</p>
              <div className="space-y-2">
                {g.people.map(p => (
                  <div key={p.id} className="flex items-center justify-between text-xs bg-slate-50 rounded-lg px-3 py-2">
                    <span>{p.email || 'no email'} · {(p.organizations || []).join('; ') || 'no org'} · sources: {(p.sources || []).map(s => s.system).join(', ')}</span>
                    <span className="flex gap-1">
                      {g.people.filter(x => x.id !== p.id).map(other => (
                        <button key={other.id} onClick={() => merge(p.id, other.id)} data-testid={`impact-merge-${p.id}`}
                          className="flex items-center gap-1 px-2 py-1 rounded border border-slate-300 hover:bg-white font-semibold" title={`Keep this record, merge "${other.email || 'no email'}" into it`}>
                          <GitMerge className="w-3 h-3" /> Keep this one
                        </button>
                      ))}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="impact-person-detail">
            <div className="px-6 py-4 flex items-start justify-between text-white rounded-t-xl" style={{ background: BRAND.navy }}>
              <div>
                <h3 className="font-bold text-lg" style={{ fontFamily: 'Roboto, Arial, sans-serif' }}>{detail.name}</h3>
                <p className="text-xs text-slate-300">{detail.email || 'No email on file'} · {(detail.organizations || []).join('; ') || 'No organization recorded'}</p>
                {(detail.roles || []).length > 0 && <p className="text-xs mt-1" style={{ color: BRAND.gold }}>{detail.roles.join(' · ')}</p>}
              </div>
              <button onClick={() => setDetail(null)} className="text-slate-300 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6">
              {detail.merged_records?.length > 0 && (
                <div className="mb-4 text-xs bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center justify-between">
                  <span>{detail.merged_records.length} record(s) merged into this person.</span>
                  <button onClick={async () => {
                    const r = await fetch(`${API_URL}/api/impact/people/${detail.merged_records[0]}/unmerge`, { method: 'POST', headers: getAuthHeader() });
                    if (r.ok) { toast.success('Unmerged'); setDetail(null); fetchPeople(); }
                  }} className="flex items-center gap-1 font-semibold text-amber-700"><Undo2 className="w-3 h-3" /> Unmerge</button>
                </div>
              )}
              <h4 className="text-xs font-black uppercase tracking-widest text-slate-400 mb-3">Participation history ({detail.history.length})</h4>
              <div className="space-y-2">
                {detail.history.map((h, i) => (
                  <div key={i} className="border border-slate-200 rounded-lg px-4 py-3 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold" style={{ color: BRAND.navy }}>{h.event_title || h.kind}</span>
                      <span className="text-xs text-slate-400">{h.event_date || h.date || 'Undated'}</span>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-1.5 text-[11px]">
                      <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{h.program_name}</span>
                      <span className="px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 capitalize">{h.registration_status}</span>
                      <span className={`px-2 py-0.5 rounded-full ${h.attendance_status === 'not_recorded' ? 'bg-slate-100 text-slate-400' : 'bg-green-50 text-green-700'}`}>
                        Attendance: {STATUS_LABEL[h.attendance_status] || h.attendance_status}
                      </span>
                      {h.certificate_status && <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">Certificate: {h.certificate_status}</span>}
                      <span className="px-2 py-0.5 rounded-full bg-slate-50 text-slate-400">Source: {h.source_system}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
